import React from 'react';
import {
  Alert,
  AlertActionLink,
  Button,
  Checkbox,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Divider,
  Flex,
  FlexItem,
  Spinner,
  Title,
  Tooltip,
  WizardFooterWrapper,
  useWizardContext,
} from '@patternfly/react-core';
import { PencilAltIcon } from '@patternfly/react-icons';
import type { GpuServerImage, GpuServerOfferLocation, GpuServerOfferProduct } from '../../../../types/models/GpuServer';
import type { PubkeySummary } from '../../../../types/models/Pubkey';
import type { Wallet } from '../../../../types/models/Wallet';
import { formatPrice, formatUnitPrice } from '../../../../utils/money';
import { BalanceAmount } from '../../../Organization/Billing/paymentDisplay';
import { GPU_SERVER_START_UNCONFIRMED_MESSAGE } from '../../../../hooks/useStartGpuServer';
import { vatLabel } from '../gpuServerBilling';
import { locationName } from './gpuServerLocation';
import { ProductDescription } from './ServerStep';
import { STEP_IDS } from './startGpuServerSteps';
import type { EditableStepId } from './startGpuServerSteps';

/** The acknowledgement the customer must tick before starting (agreed copy, verbatim). */
export const START_ACKNOWLEDGEMENT =
  'I understand that this server is paid hour by hour from my prepaid balance, that it is deleted with its data when the balance cannot pay the next hour (its disk is kept 96 hours — contact support within that time if you need it back), and that shutting it down does not stop billing.';

export interface ReviewError {
  message: string;
  showTopUp: boolean;
}

interface ReviewStepProps {
  product: GpuServerOfferProduct | null;
  locationCode: string | null;
  locations: GpuServerOfferLocation[];
  image: GpuServerImage | null;
  keys: PubkeySummary[];
  /** The offer's VAT rate (`"21.00"`); informational — the start re-checks the billing profile. */
  vatRate: string;
  diskGb: number;
  wallet: Wallet | null;
  walletLoading: boolean;
  walletError: string | null;
  onRetryWallet: () => void;
  /** The servers read failed: the estimate is unavailable. */
  serversError: string | null;
  onRetryServers: () => void;
  /** ≈ this server's first hour + the next hour of the billed servers; `null` = unavailable. */
  reserve: string | null;
  /** `balance < reserve`; `null` when either is unknown. */
  shortfall: boolean | null;
  onTopUp: () => void;
  acknowledged: boolean;
  onAcknowledgedChange: (checked: boolean) => void;
  /** An earlier submit was unconfirmed: the steps are locked until a success. */
  locked: boolean;
  /** The last submit's refusal to show here (not tied to a step). */
  reviewError: ReviewError | null;
  /** The last submit returned a `failed` server (refused and refunded). */
  lastFailed: boolean;
  activeCount: number;
  maxServers: number;
}

const SectionHeader: React.FunctionComponent<{
  title: string;
  stepId: EditableStepId;
  editLabel: string;
  locked: boolean;
}> = ({ title, stepId, editLabel, locked }) => {
  const { goToStepById } = useWizardContext();
  return (
    <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }} alignItems={{ default: 'alignItemsCenter' }}>
      <FlexItem>
        <Title headingLevel="h3" size="md">
          {title}
        </Title>
      </FlexItem>
      <FlexItem>
        <Button
          variant="link"
          isInline
          icon={<PencilAltIcon />}
          onClick={() => goToStepById(stepId)}
          aria-label={editLabel}
          isDisabled={locked}
        >
          Edit
        </Button>
      </FlexItem>
    </Flex>
  );
};

/**
 * Step 5 — review and start. The estimate is an approximation shown with "≈" and never blocks the
 * start: the backend decides, and its refusal names the exact amount.
 */
const ReviewStep: React.FunctionComponent<ReviewStepProps> = ({
  product,
  locationCode,
  locations,
  image,
  keys,
  vatRate,
  diskGb,
  wallet,
  walletLoading,
  walletError,
  onRetryWallet,
  serversError,
  onRetryServers,
  reserve,
  shortfall,
  onTopUp,
  acknowledged,
  onAcknowledgedChange,
  locked,
  reviewError,
  lastFailed,
  activeCount,
  maxServers,
}) => {
  const topUpAction = <AlertActionLink onClick={onTopUp}>Top up</AlertActionLink>;

  const renderBalance = () => {
    if (wallet !== null) {
      return <BalanceAmount value={wallet.balance} />;
    }
    if (walletError !== null) {
      return 'Unavailable';
    }
    return <Spinner size="sm" aria-label="Loading your prepaid balance" />;
  };

  return (
    <>
      <Title headingLevel="h2" size="lg" className="pf-v6-u-mb-md">
        Review and start
      </Title>

      {locked && (
        <Alert variant="warning" isInline title="Start not confirmed" className="pf-v6-u-mb-md">
          <p>{GPU_SERVER_START_UNCONFIRMED_MESSAGE}</p>
          <p>Your choices stay locked until the start is confirmed.</p>
        </Alert>
      )}
      {lastFailed && (
        <Alert
          variant="danger"
          isInline
          title="The server could not be created — its first hour is refunded to your balance"
          className="pf-v6-u-mb-md"
        >
          You can try again.
        </Alert>
      )}
      {reviewError !== null && (
        <Alert
          variant="danger"
          isInline
          title={locked ? 'The retry was refused' : 'The server could not be started'}
          className="pf-v6-u-mb-md"
          actionLinks={reviewError.showTopUp ? topUpAction : undefined}
        >
          {reviewError.message}
        </Alert>
      )}
      {activeCount >= maxServers && (
        <Alert
          variant="warning"
          isInline
          title={`You already run the maximum number of GPU servers (${maxServers})`}
          className="pf-v6-u-mb-md"
        >
          Delete one before starting another. Servers that are starting or being deleted count too.
        </Alert>
      )}

      <SectionHeader title="Server" stepId={STEP_IDS.server} editLabel="Edit the server" locked={locked} />
      <DescriptionList isHorizontal isCompact className="pf-v6-u-mt-sm pf-v6-u-mb-md">
        <DescriptionListGroup>
          <DescriptionListTerm>Server</DescriptionListTerm>
          <DescriptionListDescription>
            {product?.label ?? '—'}
            {product !== null && <ProductDescription description={product.description} />}
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Location</DescriptionListTerm>
          <DescriptionListDescription>
            {locationCode !== null ? `${locationName(locationCode, locations)} (${locationCode})` : '—'}
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>System image</DescriptionListTerm>
          <DescriptionListDescription>{image !== null ? (image.name ?? image.code) : '—'}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>SSH keys</DescriptionListTerm>
          <DescriptionListDescription>
            {keys.length > 0 ? keys.map((key) => key.label).join(', ') : '—'}
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>System disk</DescriptionListTerm>
          <DescriptionListDescription>{diskGb} GB, included in the price</DescriptionListDescription>
        </DescriptionListGroup>
      </DescriptionList>

      <Divider className="pf-v6-u-my-md" />

      <Title headingLevel="h3" size="md">
        Price
      </Title>
      <DescriptionList isHorizontal isCompact className="pf-v6-u-mt-sm pf-v6-u-mb-md">
        <DescriptionListGroup>
          <DescriptionListTerm>Per hour, excl. VAT</DescriptionListTerm>
          <DescriptionListDescription>
            {product !== null ? formatUnitPrice(product.hourly_price_ht) : '—'}
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>VAT</DescriptionListTerm>
          <DescriptionListDescription>{vatLabel(vatRate)}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Per hour, incl. VAT</DescriptionListTerm>
          <DescriptionListDescription>
            <strong>{product !== null ? formatPrice(product.first_hour_ttc) : '—'}</strong>
          </DescriptionListDescription>
        </DescriptionListGroup>
      </DescriptionList>
      <Content component="p">
        Each hour is paid in advance from your prepaid balance, starting with the first hour when the server starts. The
        price stays fixed for the server&apos;s lifetime.
      </Content>

      <Divider className="pf-v6-u-my-md" />

      <Title headingLevel="h3" size="md">
        Prepaid balance
      </Title>
      {walletError !== null && (
        <Alert
          variant="warning"
          isInline
          title="Your prepaid balance could not be loaded"
          className="pf-v6-u-my-sm"
          actionLinks={<AlertActionLink onClick={onRetryWallet}>Retry</AlertActionLink>}
        >
          {walletError}
        </Alert>
      )}
      {serversError !== null && (
        <Alert
          variant="warning"
          isInline
          title="Your current GPU servers could not be loaded"
          className="pf-v6-u-my-sm"
          actionLinks={<AlertActionLink onClick={onRetryServers}>Retry</AlertActionLink>}
        >
          {serversError}
        </Alert>
      )}
      <DescriptionList isHorizontal isCompact className="pf-v6-u-mt-sm pf-v6-u-mb-sm">
        <DescriptionListGroup>
          <DescriptionListTerm>Balance</DescriptionListTerm>
          <DescriptionListDescription>
            {renderBalance()}
            {walletLoading && wallet !== null && (
              <Spinner size="sm" className="pf-v6-u-ml-sm" aria-label="Refreshing your prepaid balance" />
            )}
          </DescriptionListDescription>
        </DescriptionListGroup>
      </DescriptionList>
      <Content component="p">
        {reserve !== null
          ? `At least ≈ ${formatPrice(reserve)} needed to start (this server's first hour + the next hour of your servers).`
          : 'The amount needed to start is unavailable right now.'}
      </Content>
      {shortfall === true && reserve !== null && (
        <Alert
          variant="warning"
          isInline
          title="Your balance may not cover this start"
          className="pf-v6-u-mt-sm"
          actionLinks={topUpAction}
        >
          Your balance is below the ≈ {formatPrice(reserve)} needed to start, so the start will probably be refused. Top
          up your balance first.
        </Alert>
      )}

      <Divider className="pf-v6-u-my-md" />

      <Checkbox
        id="gpu-start-acknowledge"
        label={START_ACKNOWLEDGEMENT}
        isChecked={acknowledged}
        isRequired
        isDisabled={locked}
        onChange={(_event, checked) => onAcknowledgedChange(checked)}
      />
    </>
  );
};

interface ReviewFooterProps {
  /** "Start server", "Try again" after a failed server, or "Retry (same key)" while locked. */
  primaryLabel: string;
  canSubmit: boolean;
  /** Why Start is disabled (tooltip), or `null`. */
  blocker: string | null;
  submitting: boolean;
  locked: boolean;
  onSubmit: () => Promise<{ jumpToStepId?: EditableStepId }>;
}

/** Back / primary / Cancel, like the domain wizard's recap footer. Back is disabled while locked. */
export const ReviewFooter: React.FunctionComponent<ReviewFooterProps> = ({
  primaryLabel,
  canSubmit,
  blocker,
  submitting,
  locked,
  onSubmit,
}) => {
  const { goToPrevStep, goToStepById, close } = useWizardContext();

  const handleSubmit = async () => {
    if (!canSubmit || submitting) {
      return;
    }
    const { jumpToStepId } = await onSubmit();
    if (jumpToStepId) {
      goToStepById(jumpToStepId);
    }
  };

  const primary = (
    <Button
      variant="primary"
      onClick={() => void handleSubmit()}
      isAriaDisabled={!canSubmit && !submitting}
      isDisabled={submitting}
      isLoading={submitting}
    >
      {primaryLabel}
    </Button>
  );

  return (
    <WizardFooterWrapper>
      <Button variant="secondary" onClick={() => void goToPrevStep()} isDisabled={submitting || locked}>
        Back
      </Button>
      {!canSubmit && !submitting && blocker !== null ? <Tooltip content={blocker}>{primary}</Tooltip> : primary}
      <Button variant="link" onClick={() => close()} isDisabled={submitting}>
        Cancel
      </Button>
    </WizardFooterWrapper>
  );
};

export default ReviewStep;
